import { PageOptions } from '@graphcommerce/framer-next-pages'
import { HygraphPagesQuery, hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import {
  PageMeta,
  MetaRobots,
  GetStaticProps,
  Button,
  IconSvg,
  iconPlay,
  responsiveVal,
  LayoutTitle,
  findByTypename,
  useStorefrontConfig,
} from '@graphcommerce/next-ui'
import iconDownload from '@graphcommerce/next-ui/icons/download.svg'
import { Trans } from '@lingui/react'
import { Box, Container, Typography } from '@mui/material'
import { GetStaticPaths } from 'next'
import {
  DiyList,
  LayoutNavigation,
  LayoutNavigationProps,
  RowBlogContent,
  RowRenderer,
  DiyPathsDocument,
  LayoutHeaderVaessen,
} from '../../components'
import { DiyListDocument, DiyListQuery } from '../../components/Diy/DiyList.gql'
import { DiyStep, DiyStepProps } from '../../components/DiyStep'
import { RowBannerSlider } from '../../components/GraphCMS/RowBannerSlider/RowBannerSlider'
import { RowColumnOne } from '../../components/GraphCMS/RowColumnOne/RowColumnOne'
import { RowColumnThree } from '../../components/GraphCMS/RowColumnThree/RowColumnThree'
import { RowColumnTwo } from '../../components/GraphCMS/RowColumnTwo/RowColumnTwo'
import { RowProductList } from '../../components/GraphCMS/RowProductList/RowProductList'
import { setTitle, setTitleClean } from '../../components/GraphCMS/utils'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { GetProductsBySkuDocument, GetProductsBySkuQuery } from '../../graphql/GetProductsBySku.gql'
import { graphqlSsrClient, graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'

export const config = { unstable_JsPreload: false }

type Props = HygraphPagesQuery & DiyListQuery & GetProductsBySkuQuery
type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

const renderAs = {
  RowColumnOne,
  RowColumnTwo,
  RowColumnThree,
  RowBlogContent,
}

function IntroText(props) {
  const { renderType, componentProps } = props
  const Component = renderAs[renderType]
  return <Component {...componentProps} />
}

function DIYPage(props: Props) {
  const { pages, diyPosts, productsBySku } = props
  const page = pages[0]
  const title = page.title ?? ''
  const titleClean = setTitleClean(title)
  const storeB2B = useStorefrontConfig().magentoStoreCode.includes('b2b')
  const { pdf } = page

  const metaTitle = page.metaTitle ?? titleClean
  const metaDescription = page?.metaDescription ?? ''
  const metaRobots =
    (page?.metaRobots.toLowerCase().split('_').flat(1) as MetaRobots[]) ?? undefined
  const canonical = page?.url ? `/${page.url}` : undefined

  let filteredPage = { ...page }
  let { content } = filteredPage
  /* The Diy Page follows a somewhat fixed structure
   * - BannerSlider (if present)
   * - First content block (RowColumnOne, RowColumnTwo, ...)
   * - DIY Steps
   * - Other Row items rendered by RowRenderer
   *
   * Because we want more fine-grained control over how and when to render certain content blocks we extract these from our page's 'content' object into seperate variables
   *
   * */

  // extract diySteps to seperate array and remove from 'content'
  const diySteps = content.filter((item) => item.__typename === 'DiyStep') as DiyStepProps[]
  content = content.filter((item) => item.__typename !== 'DiyStep')

  // if the first content type is a RowBannerSlider we actually want to show it full width and outside of the normal content renderer
  const heroBanner = content[0].__typename === 'RowBannerSlider' ? content[0] : null
  if (heroBanner) {
    content = content.slice(1, content.length)
  }

  // extract first content section and remove from 'content'
  const firstContentBlock =
    content[0].__typename.startsWith('RowColumn') || content[0].__typename.startsWith('RowBlog')
      ? content[0]
      : null
  if (firstContentBlock) {
    content = content.slice(1, content.length)
  }

  // update our filterdPage with the modified content, let the RowRenderer take care of the rest
  filteredPage = {
    ...filteredPage,
    content,
  }

  let hasVideo
  let hasProducts

  Object.keys(content).forEach((thing) => {
    if (content[thing].__typename === 'RowVideo') {
      hasVideo = true
    }
    if (content[thing].__typename === 'RowProductList') {
      hasProducts = true
    }
  })

  const viewAllButtonCaption = "DIY's"

  return (
    <>
      <PageMeta
        title={metaTitle}
        metaDescription={metaDescription}
        metaRobots={storeB2B ? ['noindex', 'nofollow'] : metaRobots}
        canonical={canonical}
      />

      <LayoutHeaderVaessen floatingMd>
        <LayoutTitle component='span' size='small'>
          {titleClean}
        </LayoutTitle>
      </LayoutHeaderVaessen>

      {heroBanner && (
        <RowBannerSlider
          identity={heroBanner?.identity}
          bannerSliderSlides={heroBanner?.bannerSliderSlides}
          sx={{ mb: 0 }}
        />
      )}

      <Container maxWidth='md'>
        <Box sx={(theme) => ({ mt: theme.spacings.xl, mb: theme.spacings.md })}>
          <Typography
            component='h1'
            variant='h1'
            align='center'
            dangerouslySetInnerHTML={{ __html: setTitle(title) }}
          />
        </Box>
      </Container>

      {firstContentBlock && (
        <Box
          sx={(theme) => ({
            mx: 'auto',
            textAlign: 'center',
            width: '100%',
            maxWidth: theme.breakpoints.values.md,
            '& > div': {
              mb: theme.spacings.md,
            },
          })}
        >
          <IntroText renderType={firstContentBlock.__typename} componentProps={firstContentBlock} />
        </Box>
      )}

      <Container
        sx={(theme) => ({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: responsiveVal(10, 20),
          marginBottom: theme.spacings.xl,
        })}
      >
        {pdf && (
          <Button
            startIcon={<IconSvg src={iconDownload} />}
            variant='text'
            color='primary'
            href={pdf.url}
            target='_blank'
          >
            <Trans id='Download PDF' />
          </Button>
        )}

        {hasVideo && (
          <Button
            startIcon={<IconSvg src={iconPlay} fillIcon />}
            variant='text'
            color='primary'
            onClick={() => {
              document
                .querySelector('iframe[src*="youtube"]')
                ?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            <Trans id='Video' />
          </Button>
        )}

        {hasProducts && (
          <Button
            variant='text'
            color='primary'
            onClick={() => {
              document.querySelector('.SidebarSlider-grid')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            <Trans id='Required items' />
          </Button>
        )}
      </Container>

      {diySteps.length > 0 && (
        <Container sx={(theme) => ({ marginBottom: theme.spacings.lg })}>
          <Box
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: '1fr',
              gridGap: theme.spacings.sm,
              [theme.breakpoints.up('lg')]: {
                gridTemplateColumns: 'repeat(4, 1fr)',
              },
              [theme.breakpoints.up('sm')]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
              [theme.breakpoints.up('xs')]: {
                gridTemplateColumns: '1fr 1fr',
              },
            })}
          >
            {diySteps.map((diyStep, index) => (
              <DiyStep key={diyStep.id} {...diyStep} index={index} />
            ))}
          </Box>
        </Container>
      )}

      <RowRenderer
        {...filteredPage}
        renderer={{
          RowProductList: (rowProps) => (
            <RowProductList {...rowProps} productsBySku={productsBySku} />
          ),
        }}
      />

      <Container>
        <Typography
          component='h2'
          variant='h1'
          sx={(theme) => ({ textAlign: 'center', mb: theme.spacings.md })}
          dangerouslySetInnerHTML={{ __html: setTitle('YOU MAY ALSO [[like]]') }}
        />
      </Container>
      <DiyList diyPosts={diyPosts} />
      <Container>
        <Box sx={{ display: 'flex', placeContent: 'center' }}>
          <Button href='/diy' variant='outlined' color='secondary' sx={{ boxShadow: 4 }}>
            <Trans id='View all {name}' values={{ name: viewAllButtonCaption }} />
          </Button>
        </Box>
      </Container>
    </>
  )
}

DIYPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default DIYPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  return { paths: [], fallback: 'blocking' } // no SSG for now!
  if (import.meta.graphCommerce.limitSsg) return { paths: [], fallback: 'blocking' }

  const responses = locales.map(async (locale) => {
    const staticClient = graphqlSsrClient(locale)
    const DiyPaths = staticClient.query({ query: DiyPathsDocument })
    const { pages } = (await DiyPaths).data
    return (
      pages.map((page) => ({ params: { url: `${page?.url}`.replace('diy/', '') }, locale })) ?? []
    )
  })
  const paths = (await Promise.all(responses)).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const urlKey = params?.url ?? '??'
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const limit = 3
  const conf = client.query({ query: StoreConfigDocument })
  const page = hygraphPageContent(staticClient, `diy/${urlKey}`)

  const diyPosts = staticClient.query({
    query: DiyListDocument,
    variables: { currentUrl: [`diy/${urlKey}`], first: limit },
  })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  if (!(await page).data.pages?.[0]) {
    return redirectOrNotFound(staticClient, conf, { url: `diy/${urlKey}` }, locale)
  }

  const singlePage = (await page).data.pages?.[0]
  const skus = findByTypename(singlePage.content, 'RowProductList')
    ?.skUs.replace(/\s/g, '')
    .split(',')

  const productsForList = staticClient.query({
    query: GetProductsBySkuDocument,
    variables: { skus },
  })

  return {
    props: {
      ...(await page).data,
      ...(await diyPosts).data,
      ...(await layout).data,
      ...(await productsForList).data,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
